import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { PasswordModule } from 'primeng/password';
import { Toast } from 'primeng/toast';
import { AuthResponse } from 'src/app/shared/interfaces/auth.response.interface';
import { AuthService } from 'src/app/shared/services/auth.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
      MessageModule,
      CardModule, ButtonModule, CommonModule, PasswordModule, FormsModule, ReactiveFormsModule, Toast, InputTextModule],
    selector: 'app-login',
    templateUrl: './login.component.html',
    providers: [MessageService],
    styles: [
        `
    :host ::ng-deep .p-inputtext {
      width: 100%;
    }
    `
    ]
})
export class LoginComponent implements OnInit {

  public showMessage: boolean;
  public message: string;
  public tempToken = '';

  private authResp: AuthResponse;

  public loginForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]]
  });

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private router: Router,
    private ms: MessageService
  ) {}

  ngOnInit(): void {
    this.loginForm.reset();
    this.showMessage = false;
    this.authResp = { uid: '', error: '' };

    this.loadRecaptchaScript().then(() => {
      this.executeRecaptcha();
    });
  }

  private loadRecaptchaScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.getElementById('recaptcha-script')) {
        resolve(); // Ya está cargado
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?render=6LdoWlopAAAAALD9iUZMaml6DBhnrfWTKdBDf782';
      script.id = 'recaptcha-script';
      script.onload = () => resolve();
      script.onerror = () => reject('No se pudo cargar el script de reCAPTCHA');
      document.body.appendChild(script);
    });
  }

  private executeRecaptcha() {
    (window as any).grecaptcha.ready(() => {
      (window as any).grecaptcha
        .execute('6LdoWlopAAAAALD9iUZMaml6DBhnrfWTKdBDf782', { action: 'importantAction' })
        .then((token: string) => {
          this.tempToken = token;
        });
    });
  }

  isValidField(field: string): boolean | null {
    return this.loginForm.controls[field].errors && this.loginForm.controls[field].touched;
  }

  async onSubmit() {
    if (this.tempToken === '') {
      this.ms.add({ severity: 'warn', summary: 'Advertencia', detail: 'Verificación reCAPTCHA no válida' });
      return;
    }

    const resp = await this.auth.login(
      this.loginForm.controls['email'].value,
      this.loginForm.controls['password'].value
    );

    resp.subscribe(ar => {
      this.authResp = ar;
      console.log(this.authResp);
      if (ar.error !== '') {
        if (ar.error?.includes('invalid-credential')) {
          this.ms.add({ severity: 'error', summary: 'Error', detail: 'Credenciales incorrectas' });
          this.loginForm.controls['password'].reset();
          return;
        }
      }

      if (ar.uid !== '') {
        this.auth.checkUser();
        if (this.auth.currentUser()?.firstSession) {
          return this.router.navigate(['auth/alta-empleado']);
        }
        return this.router.navigate(['app']);
      } else {
        this.ms.add({ severity: 'error', summary: 'Error', detail: 'Se ha producido un error, por favor inténtalo de nuevo' });
        return;
      }
    });
  }

  forgottenPassword() {
    const email = this.loginForm.value.email;
    this.router.navigate(['./auth/recover/'], { queryParams: { email: email } });
  }
}
